// i18n.js — lightweight English-key bilingual (EN/TH) layer for the Vendor OS.
//
// Design: the English string IS the key. `t("Suppliers")` returns the Thai
// translation when the language is Thai and one exists, otherwise the English
// string verbatim. This lets us retrofit a large existing app incrementally —
// any string we haven't translated yet stays readable English instead of
// showing a missing-key placeholder.
//
// Usage in a component:
//   const t = useT();
//   <h1>{t("Suppliers")}</h1>
//   t("{n} vendors", { n: count })     // interpolation
//
// Toggle: const { lang, setLang } = useLang();

import React, { createContext, useContext, useState, useCallback } from 'react';

const LANG_KEY = 'vendoros:lang';

// English → Thai. Add entries here as strings get wrapped in t(). Missing keys
// fall back to the English text, so partial coverage degrades gracefully.
export const TH = {
  // ---- Shell / chrome ----
  'Internal team': 'ทีมภายใน',
  'Internal': 'ภายใน',
  'Supplier': 'ซัพพลายเออร์',
  'Procurement': 'ฝ่ายจัดซื้อ',
  'Sign out': 'ออกจากระบบ',
  'Staff member': 'พนักงาน',
  'Search suppliers, invoices, contracts…': 'ค้นหาซัพพลายเออร์ ใบแจ้งหนี้ สัญญา…',
  'Language': 'ภาษา',

  // ---- Internal nav ----
  'Suppliers': 'ซัพพลายเออร์',
  'Approval queue': 'คิวอนุมัติ',
  'Quality reviews': 'การประเมินคุณภาพ',
  'Billing inbox': 'กล่องวางบิล',
  'Tools': 'เครื่องมือ',
  'Audit log': 'บันทึกการตรวจสอบ',
  'Reports': 'รายงาน',

  // ---- Portal nav ----
  'Dashboard': 'แดชบอร์ด',
  'Submit invoice': 'ส่งใบแจ้งหนี้',
  'My billing': 'การวางบิลของฉัน',
  'Company profile': 'ข้อมูลบริษัท',
  'Documents': 'เอกสาร',
  'Settings': 'ตั้งค่า',

  // ---- Roles ----
  'Admin': 'ผู้ดูแลระบบ',
  'Pm': 'PM',
  'Finance': 'การเงิน',
  'Viewer': 'ผู้ดูข้อมูล',

  // ---- Screen titles ----
  'Supplier detail': 'ข้อมูลซัพพลายเออร์',
  'Register supplier': 'ลงทะเบียนซัพพลายเออร์',
  'Register new supplier': 'ลงทะเบียนซัพพลายเออร์ใหม่',

  // ---- Invoice statuses ----
  'PM review': 'รอ PM ตรวจสอบ',
  'Finance verify': 'รอการเงินตรวจสอบ',
  'Approved for payment': 'อนุมัติจ่ายแล้ว',
  'Scheduled': 'กำหนดจ่ายแล้ว',
  'Paid': 'จ่ายแล้ว',
  'Rejected': 'ปฏิเสธ',
  'Disputed': 'มีข้อโต้แย้ง',

  // ---- Vendor status / compliance ----
  'Under review': 'อยู่ระหว่างตรวจสอบ',
  'Active': 'ใช้งานอยู่',
  'Preferred': 'ซัพพลายเออร์แนะนำ',
  'Blacklisted': 'บัญชีดำ',
  'KYC pending': 'รอตรวจ KYC',
  'Verified': 'ยืนยันแล้ว',
  'Individual': 'บุคคลธรรมดา',
  'Company': 'นิติบุคคล',
  'Approved': 'อนุมัติแล้ว',
  'Rejected / hold': 'ปฏิเสธ / ระงับ',

  // ---- More categories ----
  'Office Supplies': 'เครื่องใช้สำนักงาน',
  'Legal': 'กฎหมาย',

  // ---- Screen headers / subtitles ----
  'New invoice': 'ใบแจ้งหนี้ใหม่',
  'Submit for review': 'ส่งเพื่อตรวจสอบ',
  'Submitting…': 'กำลังส่ง…',
  'No invoices submitted yet': 'ยังไม่มีใบแจ้งหนี้ที่ส่ง',
  'All invoices submitted to MotionReach · synced just now': 'ใบแจ้งหนี้ทั้งหมดที่ส่งให้ MotionReach · ซิงค์เมื่อสักครู่',
  'Awaiting review': 'รอการตรวจสอบ',
  'Approved · awaiting pay': 'อนุมัติแล้ว · รอจ่าย',
  'In review': 'อยู่ระหว่างตรวจสอบ',
  'Needs attention': 'ต้องดำเนินการ',
  'Search invoices…': 'ค้นหาใบแจ้งหนี้…',
  'PO': 'เลขที่ PO',
  'Submitting as {name}': 'ส่งในนาม {name}',
  'Routes to PM review automatically': 'ส่งเข้าสู่การตรวจสอบของ PM โดยอัตโนมัติ',
  'Submit your first invoice to MotionReach to see it appear here.': 'ส่งใบแจ้งหนี้ใบแรกให้ MotionReach เพื่อแสดงที่นี่',
  '{n} in progress': 'กำลังดำเนินการ {n} ใบ',
  '{n} scheduled': 'กำหนดจ่าย {n} ใบ',
  '{n} paid': 'จ่ายแล้ว {n} ใบ',
  ' · avg {d} days': ' · เฉลี่ย {d} วัน',
  '{n} need your input': 'ต้องการข้อมูลจากคุณ {n} ใบ',
  'Suppliers indexed by Procurement': 'ซัพพลายเออร์ที่จัดทำดัชนีโดยฝ่ายจัดซื้อ',
  'PMs and AEs score suppliers at project closure · rolling 12-month average': 'PM และ AE ให้คะแนนซัพพลายเออร์เมื่อปิดโปรเจกต์ · ค่าเฉลี่ยย้อนหลัง 12 เดือน',
  'Live activity across vendors, invoices, approvals and compliance': 'กิจกรรมแบบเรียลไทม์ของซัพพลายเออร์ ใบแจ้งหนี้ การอนุมัติ และการปฏิบัติตามข้อกำหนด',
  'Spend, payment cycles and supplier performance': 'ยอดใช้จ่าย รอบการจ่ายเงิน และประสิทธิภาพของซัพพลายเออร์',
  'Search invoices, projects…': 'ค้นหาใบแจ้งหนี้ โปรเจกต์…',
  'Supplier portal': 'พอร์ทัลซัพพลายเออร์',
  'Account': 'บัญชี',
  'Vendor portal': 'พอร์ทัลซัพพลายเออร์',
  'Vendor': 'ซัพพลายเออร์',

  // ---- Categories ----
  'Production': 'โปรดักชัน',
  'Print & Signage': 'งานพิมพ์และป้าย',
  'Media Buying': 'การซื้อสื่อ',
  'Talent & MCs': 'นักแสดงและพิธีกร',
  'Event Crew': 'ทีมงานอีเวนต์',
  'Logistics': 'โลจิสติกส์',
  'KOL / Influencer': 'KOL / อินฟลูเอนเซอร์',
  'PR / Publication': 'ประชาสัมพันธ์ / สื่อสิ่งพิมพ์',
  'General Services / Freelance': 'บริการทั่วไป / ฟรีแลนซ์',
  'All categories': 'ทุกหมวดหมู่',
  'All statuses': 'ทุกสถานะ',

  // ---- Common buttons / actions ----
  'Export': 'ส่งออก',
  'New supplier': 'เพิ่มซัพพลายเออร์',
  'Save': 'บันทึก',
  'Cancel': 'ยกเลิก',
  'Submit': 'ส่ง',
  'Approve': 'อนุมัติ',
  'Reject': 'ปฏิเสธ',
  'Dispute': 'แจ้งข้อโต้แย้ง',
  'Edit profile': 'แก้ไขข้อมูล',
  'Verify KYC': 'ยืนยัน KYC',
  'Revoke KYC': 'ยกเลิกการยืนยัน KYC',
  'Send KYC link': 'ส่งลิงก์ KYC',
  'Send portal link': 'ส่งลิงก์พอร์ทัล',
  'New review': 'เพิ่มรีวิว',
  'Upload document': 'อัปโหลดเอกสาร',
  'Clear': 'ล้าง',
  'Sort': 'เรียงลำดับ',
  'View': 'มุมมอง',
  'Schedule pay run': 'จัดรอบการจ่ายเงิน',
  'Download': 'ดาวน์โหลด',
  'Loading…': 'กำลังโหลด…',
  'Saving…': 'กำลังบันทึก…',
  'Issuing…': 'กำลังสร้างลิงก์…',
  'Submit for approval': 'ส่งเพื่ออนุมัติ',
  '{n} vendors': 'ซัพพลายเออร์ {n} ราย',
  '{n} new': 'ใหม่ {n} ราย',
  'Your team enters company info; KYC docs can be uploaded by the supplier via a link.':
    'ทีมของคุณกรอกข้อมูลบริษัท ส่วนเอกสาร KYC ให้ซัพพลายเออร์อัปโหลดผ่านลิงก์ได้',
  'Every action across vendors, invoices and accounts · retained 7 years':
    'ทุกการกระทำของซัพพลายเออร์ ใบแจ้งหนี้ และบัญชี · เก็บไว้ 7 ปี',
  'Spend, payment cycle & supplier performance · MotionReach × Brandstorm consolidated':
    'ยอดใช้จ่าย รอบการจ่ายเงิน และประสิทธิภาพซัพพลายเออร์ · รวม MotionReach × Brandstorm',

  // ---- Suppliers screen ----
  'indexed by Procurement': 'จัดทำดัชนีโดยฝ่ายจัดซื้อ',
  'Loading suppliers…': 'กำลังโหลดซัพพลายเออร์…',
  'ID': 'รหัส',
  'Status': 'สถานะ',
  'Terms': 'เงื่อนไข',
  'KYC': 'KYC',
  'Rating': 'คะแนน',
  'Spend YTD': 'ยอดใช้จ่ายปีนี้',
  'Proj.': 'โปรเจกต์',
  'Updated': 'อัปเดต',
  'Category': 'หมวดหมู่',

  // ---- Billing inbox ----
  'All supplier invoices in motion · Finance & PM review queue': 'ใบแจ้งหนี้ซัพพลายเออร์ทั้งหมด · คิวตรวจสอบของการเงินและ PM',
  'Awaiting PM review': 'รอ PM ตรวจสอบ',
  'Finance verify': 'รอการเงินตรวจสอบ',
  'Approved · ready to pay': 'อนุมัติแล้ว · พร้อมจ่าย',
  'Disputed / rejected': 'ข้อโต้แย้ง / ปฏิเสธ',
  'All': 'ทั้งหมด',
  'PM Approve': 'PM อนุมัติ',
  'Approve to pay': 'อนุมัติให้จ่าย',
  'Mark scheduled': 'ตั้งกำหนดจ่าย',
  'Mark paid': 'ทำเครื่องหมายว่าจ่ายแล้ว',
  'Charge to company · Account Payable': 'เรียกเก็บกับบริษัท · เจ้าหนี้การค้า',
  'Select company…': 'เลือกบริษัท…',
  'Internal note (required to reject or dispute)': 'บันทึกภายใน (ต้องระบุเมื่อปฏิเสธหรือโต้แย้ง)',
  'Awaiting PM approval': 'รอ PM อนุมัติ',
  'Awaiting Finance': 'รอการเงิน',
  'avg {n}h old': 'เฉลี่ยเก่า {n} ชม.',
  'avg {n}d old': 'เฉลี่ยเก่า {n} วัน',
  '{n} high-value': 'มูลค่าสูง {n} รายการ',
  'Next pay run: {date}': 'รอบจ่ายถัดไป: {date}',

  // ---- Approval queue ----
  'awaiting review · SLA target 24h': 'รอการตรวจสอบ · เป้าหมาย SLA 24 ชม.',
  'New vendors': 'ซัพพลายเออร์ใหม่',
  'Billing docs': 'เอกสารวางบิล',
  'Profile changes': 'การแก้ไขข้อมูล',
  'Auto-checks': 'การตรวจอัตโนมัติ',
  'Banking': 'ข้อมูลธนาคาร',
  'Review note (required for rejection)': 'หมายเหตุการตรวจสอบ (ต้องระบุเมื่อปฏิเสธ)',
  'Approve vendor': 'อนุมัติซัพพลายเออร์',

  // ---- Portal: dashboard / billing / submit ----
  'Next payment': 'การจ่ายครั้งถัดไป',
  'In review': 'อยู่ระหว่างตรวจสอบ',
  'Paid': 'จ่ายแล้ว',
  'Paid YTD': 'จ่ายแล้วปีนี้',
  'Avg days to pay': 'จำนวนวันเฉลี่ยที่ได้รับเงิน',
  'Hello, {name} 👋': 'สวัสดี {name} 👋',
  '{n} invoices on file': 'ใบแจ้งหนี้ในระบบ {n} ใบ',
  '{n} invoices': 'ใบแจ้งหนี้ {n} ใบ',
  'next payment {date}': 'จ่ายครั้งถัดไป {date}',
  'no upcoming payments': 'ไม่มีการจ่ายที่กำลังจะถึง',
  'View all invoices': 'ดูใบแจ้งหนี้ทั้งหมด',
  'Submit new invoice': 'ส่งใบแจ้งหนี้ใหม่',
  'nothing scheduled': 'ยังไม่มีกำหนด',
  'first year': 'ปีแรก',
  'vs last year': 'เทียบปีก่อน',
  'Submit a new invoice': 'ส่งใบแจ้งหนี้ใหม่',
  'No invoices yet': 'ยังไม่มีใบแจ้งหนี้',
  'Invoice': 'ใบแจ้งหนี้',
  'Project': 'โปรเจกต์',
  'Amount': 'จำนวนเงิน',
  'Due': 'ครบกำหนด',
  'Submitted': 'ส่งเมื่อ',

  // ---- Documents / KYC ----
  'KYC & compliance documents': 'เอกสาร KYC และการปฏิบัติตามข้อกำหนด',
  'Required documents': 'เอกสารที่ต้องใช้',
  'Document type': 'ประเภทเอกสาร',
  'Expiry date': 'วันหมดอายุ',
  'File': 'ไฟล์',
  'on file': 'มีในระบบ',
  'needed': 'ยังขาด',
  'National ID copy': 'สำเนาบัตรประชาชน',
  'Bank book': 'สมุดบัญชีธนาคาร',
  'Business registration': 'หนังสือรับรองบริษัท',
  'VAT certificate': 'ใบทะเบียนภาษีมูลค่าเพิ่ม (ภ.พ.20)',
  'Rate card': 'อัตราค่าบริการ',
  'Company profile': 'โปรไฟล์บริษัท',
  'Insurance': 'ประกันภัย',
  'Other': 'อื่นๆ',
  'optional': 'ไม่บังคับ',
  'account': 'บัญชี',
  '(PDF or image · max 20 MB)': '(PDF หรือรูปภาพ · สูงสุด 20 MB)',
  '(PDF or image · max 20 MB each)': '(PDF หรือรูปภาพ · สูงสุด 20 MB ต่อไฟล์)',
  'Uploading…': 'กำลังอัปโหลด…',
  'Uploads are added to your MotionReach vendor file and reviewed by Procurement.':
    'ไฟล์ที่อัปโหลดจะถูกเพิ่มในแฟ้มซัพพลายเออร์ของ MotionReach และตรวจสอบโดยฝ่ายจัดซื้อ',

  // ---- Misc empty/loading states ----
  'No documents on file yet.': 'ยังไม่มีเอกสารในระบบ',
  'Loading documents…': 'กำลังโหลดเอกสาร…',

  // ---- Submit invoice form ----
  'Invoice document': 'เอกสารใบแจ้งหนี้',
  'PDF only · max 20 MB': 'PDF เท่านั้น · สูงสุด 20 MB',
  'Proof of performance': 'หลักฐานการส่งมอบงาน',
  'photos · PDFs · Office docs · max 20 MB each': 'รูปภาพ · PDF · เอกสาร Office · สูงสุด 20 MB ต่อไฟล์',
  'Attach evidence that the service was actually delivered — site photos, delivery notes, signed proof-of-delivery, completion reports. The PM and Finance reviewers will see these when approving your invoice.':
    'แนบหลักฐานว่างานถูกส่งมอบจริง — รูปหน้างาน ใบส่งของ ใบรับมอบงานที่ลงนาม รายงานปิดงาน ผู้ตรวจสอบฝ่าย PM และการเงินจะเห็นไฟล์เหล่านี้ตอนอนุมัติใบแจ้งหนี้ของคุณ',
  'Remove': 'ลบ',
  'Invoice details': 'รายละเอียดใบแจ้งหนี้',
  'All fields required': 'กรอกทุกช่อง',
  'PO number': 'เลขที่ PO',
  'Invoice date': 'วันที่ในใบแจ้งหนี้',
  'The date printed on your invoice document.': 'วันที่ที่พิมพ์บนเอกสารใบแจ้งหนี้ของคุณ',
  'Payment due date': 'วันครบกำหนดชำระ',
  'Amount THB': 'จำนวนเงิน (บาท)',
  'Amount must be a number greater than zero (e.g. 45000 or 45,000.00).': 'จำนวนเงินต้องเป็นตัวเลขและมากกว่าศูนย์ (เช่น 45000 หรือ 45,000.00)',
  'VAT must be a number (enter 0 if not applicable).': 'VAT ต้องเป็นตัวเลข (กรอก 0 หากไม่มี)',
  'Withholding tax must be a number (enter 0 if not applicable).': 'ภาษีหัก ณ ที่จ่าย ต้องเป็นตัวเลข (กรอก 0 หากไม่มี)',
  '{field} is required.': 'กรุณากรอก "{field}"',
  'Invoice document (PDF) is required.': 'กรุณาแนบเอกสารใบแจ้งหนี้ (PDF)',
  'At least one proof-of-performance file is required (site photo, delivery note, etc).':
    'กรุณาแนบหลักฐานการส่งมอบงานอย่างน้อย 1 ไฟล์ (เช่น รูปหน้างาน ใบส่งของ)',
  'Maximum {n} proof-of-performance files.': 'แนบหลักฐานการส่งมอบงานได้สูงสุด {n} ไฟล์',
  '{name}: must be ≤ 20 MB.': '{name}: ขนาดไฟล์ต้องไม่เกิน 20 MB',
  'PDF must be ≤ 20MB': 'ไฟล์ PDF ต้องมีขนาดไม่เกิน 20 MB',
  'Ex-VAT': 'ไม่รวม VAT',
  'Pre-VAT (net of VAT) amount. Net payable = Amount + VAT − Withholding tax.':
    'จำนวนเงินก่อนรวม VAT ยอดสุทธิที่จ่าย = จำนวนเงิน + VAT − ภาษีหัก ณ ที่จ่าย',
  'VAT THB': 'ภาษีมูลค่าเพิ่ม (บาท)',
  'Enter 0 if not applicable.': 'กรอก 0 หากไม่มี',
  'Withholding tax THB': 'ภาษีหัก ณ ที่จ่าย (บาท)',
  'Net payable': 'ยอดสุทธิที่ต้องจ่าย',
  'Amount + VAT − Withholding tax': 'จำนวนเงิน + VAT − ภาษีหัก ณ ที่จ่าย',
  'Note to MotionReach': 'หมายเหตุถึง MotionReach',
  'Anything we should flag for the reviewer?': 'มีอะไรที่ต้องการแจ้งผู้ตรวจสอบไหม?',
  'Name': 'ชื่อ',
  'Vendor ID': 'รหัสซัพพลายเออร์',
  "On submit, your invoice is created in pm-review status. The MotionReach PM verifies it against the PO, then Finance verifies tax + bank details, then it's scheduled for payment per your credit terms.":
    'เมื่อส่ง ใบแจ้งหนี้จะถูกสร้างในสถานะ "รอ PM ตรวจสอบ" PM ของ MotionReach จะตรวจกับ PO จากนั้นฝ่ายการเงินตรวจภาษีและข้อมูลธนาคาร แล้วจึงกำหนดจ่ายตามเงื่อนไขเครดิตของคุณ',

  // ---- Billing detail panel ----
  'Amount due': 'ยอดที่ต้องชำระ',
  'Progress': 'ความคืบหน้า',
  'in progress': 'กำลังดำเนินการ',
  'Dispute opened — needs your input': 'เปิดข้อโต้แย้ง — ต้องการข้อมูลจากคุณ',
  'MotionReach Finance flagged a discrepancy on this invoice. Contact your MotionReach representative for details.':
    'ฝ่ายการเงิน MotionReach พบความไม่ตรงกันในใบแจ้งหนี้นี้ โปรดติดต่อผู้ดูแลของคุณที่ MotionReach เพื่อขอรายละเอียด',
  'Attachments': 'ไฟล์แนบ',
  'No invoice document on file.': 'ไม่มีเอกสารใบแจ้งหนี้ในระบบ',
  'uploaded by you': 'อัปโหลดโดยคุณ',
  '{n} files': '{n} ไฟล์',
  'No proof-of-performance files on this invoice.': 'ไม่มีไฟล์หลักฐานการส่งมอบงานในใบแจ้งหนี้นี้',

  // ---- Company profile (portal) ----
  'Details on file for {name}': 'ข้อมูลในระบบของ {name}',
  'Your details on file with MotionReach': 'ข้อมูลของคุณที่บันทึกไว้กับ MotionReach',
  'Edit contact details': 'แก้ไขข้อมูลติดต่อ',
  'You can update your own contact details.': 'คุณสามารถแก้ไขข้อมูลติดต่อของคุณเองได้',
  "Your legal name, tax ID, and bank details are locked for compliance — email your MotionReach contact to change those, and they'll route it through Procurement & Finance.":
    'ชื่อตามกฎหมาย เลขประจำตัวผู้เสียภาษี และข้อมูลธนาคารถูกล็อกเพื่อการปฏิบัติตามข้อกำหนด — หากต้องการแก้ไขโปรดอีเมลผู้ติดต่อ MotionReach แล้วจะดำเนินการผ่านฝ่ายจัดซื้อและการเงิน',
  'Edit your contact details': 'แก้ไขข้อมูลติดต่อของคุณ',
  'Primary contact': 'ผู้ติดต่อหลัก',
  'Phone': 'โทรศัพท์',
  'LINE ID': 'LINE ID',
  'Website': 'เว็บไซต์',
  'City': 'เมือง',
  'Address': 'ที่อยู่',
  "Company name, tax ID, and bank details can't be self-edited — email your MotionReach contact to change those.":
    'ชื่อบริษัท เลขผู้เสียภาษี และข้อมูลธนาคารแก้ไขเองไม่ได้ — โปรดอีเมลผู้ติดต่อ MotionReach เพื่อแก้ไข',
  'Save changes': 'บันทึกการเปลี่ยนแปลง',
  'Legal entity': 'ข้อมูลนิติบุคคล',
  'Company name': 'ชื่อบริษัท',
  'Tax ID / VAT': 'เลขผู้เสียภาษี / VAT',
  'Contact': 'ผู้ติดต่อ',
  'Contact email': 'อีเมลผู้ติดต่อ',
  'LINE': 'LINE',
  'Country': 'ประเทศ',
  'Bank': 'ธนาคาร',
  'Account number': 'เลขที่บัญชี',
  'Account name': 'ชื่อบัญชี',
  'Commercial terms': 'เงื่อนไขทางการค้า',
  'Credit terms': 'เงื่อนไขเครดิต',
  'Credit limit': 'วงเงินเครดิต',
  'Currency': 'สกุลเงิน',
  'Active since': 'เริ่มใช้งานตั้งแต่',
  'Performance summary': 'สรุปประสิทธิภาพ',
  'Paid to you ({year})': 'จ่ายให้คุณ ({year})',
  'Projects (by PO)': 'โปรเจกต์ (ตาม PO)',
  'On-time delivery': 'ส่งมอบตรงเวลา',
  '{n} paid': 'จ่ายแล้ว {n}',
  'Active supplier': 'ซัพพลายเออร์ที่ใช้งาน',
  'Suspended': 'ระงับการใช้งาน',
  'Need a change?': 'ต้องการเปลี่ยนแปลง?',
  'Reach out to update your details or ask about an invoice.': 'ติดต่อเราเพื่อแก้ไขข้อมูลหรือสอบถามเกี่ยวกับใบแจ้งหนี้',
  'Your project manager': 'ผู้จัดการโปรเจกต์ของคุณ',
  'Accounts payable': 'ฝ่ายเจ้าหนี้การค้า',

  // ---- Documents (portal) ----
  'Invoice & compliance documents on file': 'เอกสารใบแจ้งหนี้และการปฏิบัติตามข้อกำหนดในระบบ',
  'No documents submitted yet. Invoice PDFs you upload via Submit invoice will appear here.':
    'ยังไม่มีเอกสารที่ส่ง ไฟล์ PDF ใบแจ้งหนี้ที่คุณอัปโหลดผ่าน "ส่งใบแจ้งหนี้" จะแสดงที่นี่',
  'Submitted invoice documents': 'เอกสารใบแจ้งหนี้ที่ส่งแล้ว',
  'submitted': 'ส่งเมื่อ',
  'No invoice document attached.': 'ไม่มีเอกสารใบแจ้งหนี้แนบมา',
  'uploaded by vendor': 'อัปโหลดโดยซัพพลายเออร์',
  'No proof-of-performance files. (Legacy invoice — submitted before evidence was required.)':
    'ไม่มีไฟล์หลักฐานการส่งมอบงาน (ใบแจ้งหนี้เก่า — ส่งก่อนกำหนดให้แนบหลักฐาน)',

  // ---- Tax invoice upload (portal, post-payment) + internal display ----
  'Tax invoice': 'ใบกำกับภาษี',
  'Upload tax invoice': 'อัปโหลดใบกำกับภาษี',
  'Add more files': 'เพิ่มไฟล์',
  'Uploading…': 'กำลังอัปโหลด…',
  'This invoice is paid — please upload your tax invoice / official receipt for our accounting records.':
    'ใบแจ้งหนี้นี้ชำระเงินแล้ว — กรุณาอัปโหลดใบกำกับภาษี / ใบเสร็จรับเงิน เพื่อใช้ในการบันทึกบัญชีของเรา',
  'Tax invoice uploaded — thank you!': 'อัปโหลดใบกำกับภาษีเรียบร้อยแล้ว — ขอบคุณ!',
  'PDF or a clear photo · up to {max} files · 20MB each': 'PDF หรือรูปถ่ายที่ชัดเจน · สูงสุด {max} ไฟล์ · ไฟล์ละไม่เกิน 20MB',
  'Too many files — you can add {n} more (max {max} total).': 'ไฟล์มากเกินไป — เพิ่มได้อีก {n} ไฟล์ (สูงสุด {max} ไฟล์)',
  '{name} is larger than 20MB.': '{name} มีขนาดเกิน 20MB',
  'Not yet submitted by the supplier.': 'ซัพพลายเออร์ยังไม่ได้ส่งเข้ามา',
  'Tax invoice received': 'ได้รับใบกำกับภาษีแล้ว',
  'Tax invoice not yet received': 'ยังไม่ได้รับใบกำกับภาษี',

  // ---- Settings / password (portal) ----
  'Password': 'รหัสผ่าน',
  'Current password': 'รหัสผ่านปัจจุบัน',
  'New password': 'รหัสผ่านใหม่',
  'Confirm new password': 'ยืนยันรหัสผ่านใหม่',
  'Min. 8 characters': 'อย่างน้อย 8 ตัวอักษร',
  'Re-enter': 'กรอกอีกครั้ง',
  'Forgot your current password? Sign out and use “Forgot?” on the sign-in screen to email yourself a reset link.':
    'ลืมรหัสผ่านปัจจุบัน? ออกจากระบบแล้วใช้ "ลืมรหัสผ่าน?" ที่หน้าเข้าสู่ระบบเพื่อส่งลิงก์รีเซ็ตให้ตัวเอง',
  'Updating…': 'กำลังอัปเดต…',
  'Update password': 'อัปเดตรหัสผ่าน',
  'Account, notifications & team access': 'บัญชี การแจ้งเตือน และการเข้าถึงของทีม',
  'Security': 'ความปลอดภัย',
  'Notifications': 'การแจ้งเตือน',
  'Team access': 'การเข้าถึงของทีม',
  'Your profile': 'โปรไฟล์ของคุณ',
  'Sign-in & security': 'การเข้าสู่ระบบและความปลอดภัย',
  'How we reach you': 'ช่องทางการติดต่อคุณ',
  'Who has access': 'ผู้ที่มีสิทธิ์เข้าถึง',
  'Your personal account info — visible only to your team.': 'ข้อมูลบัญชีส่วนตัวของคุณ — เห็นเฉพาะทีมของคุณเท่านั้น',
  'Update the password you use to sign in to the portal.': 'อัปเดตรหัสผ่านที่ใช้เข้าสู่ระบบพอร์ทัล',
  'Pick how you want to be notified at each step of the invoice lifecycle.': 'เลือกวิธีรับการแจ้งเตือนในแต่ละขั้นตอนของใบแจ้งหนี้',
  'Add colleagues from your company. They get their own login and the access role you choose.':
    'เพิ่มเพื่อนร่วมงานจากบริษัทของคุณ พวกเขาจะได้รับบัญชีเข้าสู่ระบบของตนเองและสิทธิ์ที่คุณกำหนด',
  'Personal info': 'ข้อมูลส่วนตัว',
  'Vendor contact': 'ผู้ติดต่อซัพพลายเออร์',
  "These details are on file in MotionReach's vendor master. To change them, contact your MotionReach representative — only admins can edit the master record.":
    'ข้อมูลเหล่านี้บันทึกอยู่ในฐานข้อมูลซัพพลายเออร์ของ MotionReach หากต้องการแก้ไขโปรดติดต่อผู้ดูแลของ MotionReach — มีเพียงผู้ดูแลระบบเท่านั้นที่แก้ไขได้',
  'Contact name': 'ชื่อผู้ติดต่อ',

  // ---- Register supplier form (staff) ----
  'Supplier information': 'ข้อมูลซัพพลายเออร์',
  'Entered by your team.': 'กรอกโดยทีมของคุณ',
  'Supplier type': 'ประเภทซัพพลายเออร์',
  'Registered company': 'นิติบุคคล',
  'Individual supplier — KYC docs: National ID copy + bank book.': 'ซัพพลายเออร์บุคคลธรรมดา — เอกสาร KYC: สำเนาบัตรประชาชน + สมุดบัญชีธนาคาร',
  'Registered company — KYC docs: business registration + bank book (VAT optional).': 'นิติบุคคล — เอกสาร KYC: หนังสือรับรองบริษัท + สมุดบัญชีธนาคาร (VAT ไม่บังคับ)',
  'Full name': 'ชื่อ-นามสกุล',
  'Primary category': 'หมวดหมู่หลัก',
  '13 digits': '13 หลัก',
  'National ID': 'เลขบัตรประชาชน',
  'Tax ID': 'เลขผู้เสียภาษี',
  'Registered address': 'ที่อยู่จดทะเบียน',
  'Notes / why this supplier': 'หมายเหตุ / เหตุผลที่เลือกซัพพลายเออร์นี้',
  'Used for invoice settlement.': 'ใช้สำหรับการชำระเงินตามใบแจ้งหนี้',
  // ---- Banking moved to supplier side (2026-06-10) ----
  'Collected from the supplier.': 'กรอกโดยซัพพลายเออร์',
  "Bank account details are now entered by the supplier themselves — they fill them in on the document-upload screen, alongside their KYC documents, via the link you send after creating them. You can still set or correct banking later from the supplier's profile if needed.":
    'ข้อมูลบัญชีธนาคารจะถูกกรอกโดยซัพพลายเออร์เอง — พวกเขากรอกในหน้าจออัปโหลดเอกสารพร้อมกับเอกสาร KYC ผ่านลิงก์ที่คุณส่งหลังจากสร้างซัพพลายเออร์ คุณยังสามารถตั้งค่าหรือแก้ไขข้อมูลธนาคารภายหลังได้จากโปรไฟล์ของซัพพลายเออร์หากจำเป็น',
  'Enter the bank account MotionReach should pay your invoices into. The account name must match your registered name.':
    'กรอกบัญชีธนาคารที่ให้ MotionReach โอนชำระใบแจ้งหนี้ของคุณ ชื่อบัญชีต้องตรงกับชื่อที่จดทะเบียนของคุณ',
  'Enter the bank account MotionReach should pay your invoices into. The account name must match your registered company.':
    'กรอกบัญชีธนาคารที่ให้ MotionReach โอนชำระใบแจ้งหนี้ของคุณ ชื่อบัญชีต้องตรงกับชื่อบริษัทที่จดทะเบียนของคุณ',
  'Banking on file — update it any time.': 'มีข้อมูลธนาคารในระบบแล้ว — แก้ไขได้ทุกเมื่อ',
  'Required before your invoices can be settled.': 'จำเป็นต้องกรอกก่อนจึงจะชำระใบแจ้งหนี้ของคุณได้',
  'Save banking details': 'บันทึกข้อมูลธนาคาร',
  'Saved ✓': 'บันทึกแล้ว ✓',
  'Banking details saved': 'บันทึกข้อมูลธนาคารแล้ว',
  'Fill in bank, account number, and account name.': 'กรุณากรอกธนาคาร เลขที่บัญชี และชื่อบัญชี',
  '— Select bank —': '— เลือกธนาคาร —',
  'Kasikorn Bank (KBank)': 'ธนาคารกสิกรไทย (KBank)',
  'Bangkok Bank (BBL)': 'ธนาคารกรุงเทพ (BBL)',
  'Siam Commercial Bank (SCB)': 'ธนาคารไทยพาณิชย์ (SCB)',
  'Krungsri Bank (BAY)': 'ธนาคารกรุงศรีอยุธยา (BAY)',
  'Krungthai Bank (KTB)': 'ธนาคารกรุงไทย (KTB)',
  'TMBThanachart Bank (TTB)': 'ธนาคารทหารไทยธนชาต (TTB)',
  'Government Savings Bank (GSB)': 'ธนาคารออมสิน (GSB)',
  'United Overseas Bank (UOB)': 'ธนาคารยูโอบี (UOB)',
  'CIMB Thai': 'ธนาคารซีไอเอ็มบี ไทย (CIMB)',
  '{bank} account numbers have {n} digits — you entered {m}.': 'เลขที่บัญชี{bank} ต้องมี {n} หลัก — คุณกรอกมา {m} หลัก',
  'Government Savings Bank accounts are 12 digits (dashes optional).': 'บัญชีธนาคารออมสินมี 12 หลัก (ใส่ขีดหรือไม่ก็ได้)',
  '10 digits (dashes optional).': '10 หลัก (ใส่ขีดหรือไม่ก็ได้)',
  'As printed on your bank book.': 'ตามที่ปรากฏในสมุดบัญชี',
  'must match': 'ต้องตรงกับ',
  'the individual': 'ชื่อบุคคล',
  'legal entity': 'ชื่อนิติบุคคล',
  'Maximum exposure approved by Finance.': 'วงเงินสูงสุดที่ฝ่ายการเงินอนุมัติ',
  'Credit limit (THB)': 'วงเงินเครดิต (บาท)',
  'KYC documents': 'เอกสาร KYC',
  'optional here': 'ไม่บังคับในขั้นตอนนี้',
  'PDF / image · max 20 MB each': 'PDF / รูปภาพ · สูงสุด 20 MB ต่อไฟล์',
  'Attach now if you have them, or skip — after you create the supplier you can send a KYC link for them to upload these themselves.':
    'แนบตอนนี้ถ้ามี หรือข้ามไปก่อน — หลังสร้างซัพพลายเออร์แล้วคุณส่งลิงก์ KYC ให้พวกเขาอัปโหลดเองได้',
  'required': 'ที่ต้องใช้',
  'National ID copy + bank book.': 'สำเนาบัตรประชาชน + สมุดบัญชีธนาคาร',
  'Business registration + bank book (VAT optional).': 'หนังสือรับรองบริษัท + สมุดบัญชีธนาคาร (VAT ไม่บังคับ)',
  'Bank book copy': 'สำเนาสมุดบัญชีธนาคาร',
  "⋆ Required for KYC — checked before approval, not at creation. Each uploaded file gets a row in the Compliance tab and lives in this supplier's Drive folder.":
    '⋆ จำเป็นสำหรับ KYC — ตรวจสอบก่อนอนุมัติ ไม่ใช่ตอนสร้าง ไฟล์ที่อัปโหลดแต่ละไฟล์จะมีแถวในแท็บ Compliance และเก็บในโฟลเดอร์ Drive ของซัพพลายเออร์นี้',
  "On submit, the supplier is created with status review and added to the Approval queue. You'll then be offered a KYC link to copy and email the supplier so they can upload their documents. Any docs you attach here are stored immediately. Procurement approves once the required KYC docs are on file.":
    'เมื่อส่ง ซัพพลายเออร์จะถูกสร้างด้วยสถานะ "อยู่ระหว่างตรวจสอบ" และเข้าคิวอนุมัติ จากนั้นระบบจะเสนอลิงก์ KYC ให้คัดลอกและอีเมลถึงซัพพลายเออร์เพื่ออัปโหลดเอกสาร เอกสารที่คุณแนบไว้จะถูกบันทึกทันที ฝ่ายจัดซื้อจะอนุมัติเมื่อมีเอกสาร KYC ครบ',
  'KYC checklist': 'รายการเอกสาร KYC',

  // ---- Login / auth screens ----
  'Your session expired. Please sign in again.': 'เซสชันของคุณหมดอายุ กรุณาเข้าสู่ระบบอีกครั้ง',
  "That Google account isn't a registered staff member. If you're a supplier, open the sign-in link we emailed you — or sign in with your supplier email & password below.":
    'บัญชี Google นี้ไม่ใช่พนักงานที่ลงทะเบียนไว้ หากคุณเป็นซัพพลายเออร์ โปรดเปิดลิงก์เข้าสู่ระบบที่เราส่งทางอีเมล — หรือเข้าสู่ระบบด้วยอีเมลและรหัสผ่านซัพพลายเออร์ของคุณด้านล่าง',
  'Sign in to your workspace': 'เข้าสู่ระบบพื้นที่ทำงานของคุณ',
  'Welcome back': 'ยินดีต้อนรับกลับมา',
  'Use your MotionReach or Brandstorm account': 'ใช้บัญชี MotionReach หรือ Brandstorm ของคุณ',
  'Sign in to your supplier account': 'เข้าสู่ระบบบัญชีซัพพลายเออร์ของคุณ',
  'SSO restricted to company domains': 'การเข้าสู่ระบบ SSO จำกัดเฉพาะโดเมนของบริษัท',
  'Access is limited to': 'การเข้าถึงจำกัดเฉพาะ',
  'and': 'และ',
  'Need access? Ask an admin to invite you.': 'ต้องการสิทธิ์เข้าถึง? ขอให้ผู้ดูแลระบบเชิญคุณ',
  'Email': 'อีเมล',
  'Password': 'รหัสผ่าน',
  'Forgot?': 'ลืมรหัสผ่าน?',
  'Keep me signed in on this device': 'จดจำการเข้าสู่ระบบบนอุปกรณ์นี้',
  'Signing in…': 'กำลังเข้าสู่ระบบ…',
  'Sign in': 'เข้าสู่ระบบ',
  'First time here? Use the Create your password link from your approval email. Not registered yet? Ask your MotionReach contact to onboard you.':
    'เข้าครั้งแรก? ใช้ลิงก์ "สร้างรหัสผ่าน" จากอีเมลอนุมัติของคุณ ยังไม่ได้ลงทะเบียน? ขอให้ผู้ติดต่อ MotionReach ของคุณช่วยลงทะเบียน',
  'Reset your password': 'รีเซ็ตรหัสผ่านของคุณ',
  "We'll email you a link to set a new one.": 'เราจะส่งลิงก์ทางอีเมลให้คุณตั้งรหัสผ่านใหม่',
  'Must match your registered MotionReach account.': 'ต้องตรงกับบัญชี MotionReach ที่ลงทะเบียนไว้',
  'Sending…': 'กำลังส่ง…',
  'Send reset link': 'ส่งลิงก์รีเซ็ต',
  'Back to sign in': 'กลับไปหน้าเข้าสู่ระบบ',
  'Need help? Email': 'ต้องการความช่วยเหลือ? อีเมล',
  'Check your inbox': 'ตรวจสอบกล่องอีเมลของคุณ',
  'If that email is registered, a reset link is on its way. It expires in 30 minutes.':
    'หากอีเมลนี้ลงทะเบียนไว้ ลิงก์รีเซ็ตกำลังถูกส่งไป ลิงก์จะหมดอายุใน 30 นาที',
  'Send again': 'ส่งอีกครั้ง',
  'Set your password': 'ตั้งรหัสผ่านของคุณ',
  'At least 8 characters.': 'อย่างน้อย 8 ตัวอักษร',
  'Confirm password': 'ยืนยันรหัสผ่าน',
  'Saving…': 'กำลังบันทึก…',
  'Set password & sign in': 'ตั้งรหัสผ่านและเข้าสู่ระบบ',
  'Welcome back, {name}': 'ยินดีต้อนรับกลับมา {name}',
  'Loading your account…': 'กำลังโหลดบัญชีของคุณ…',
  "Confirm it's you and head into your portal.": 'ยืนยันตัวตนแล้วเข้าสู่พอร์ทัลของคุณ',
  'One moment…': 'สักครู่…',
  'Enter your portal': 'เข้าสู่พอร์ทัลของคุณ',
  'Loading…': 'กำลังโหลด…',
  'Not you?': 'ไม่ใช่คุณ?',
  'Open your secure link': 'เปิดลิงก์ที่ปลอดภัยของคุณ',
  'Suppliers sign in through the link we email you.': 'ซัพพลายเออร์เข้าสู่ระบบผ่านลิงก์ที่เราส่งทางอีเมล',
  'No password needed — your sign-in link looks like': 'ไม่ต้องใช้รหัสผ่าน — ลิงก์เข้าสู่ระบบของคุณจะเป็นรูปแบบ',
  'Lost it? Reply to any MotionReach email or contact your project manager for a fresh one.':
    'ทำลิงก์หาย? ตอบกลับอีเมลใดก็ได้ของ MotionReach หรือติดต่อผู้จัดการโปรเจกต์เพื่อขอลิงก์ใหม่',
  'Internal team?': 'ทีมภายใน?',
  'Sign in with Google': 'เข้าสู่ระบบด้วย Google',
  // BrandRail body copy
  'The shared vendor system for MotionReach & Brandstorm teams — register suppliers, review invoices, and keep procurement clean.':
    'ระบบซัพพลายเออร์ที่ใช้ร่วมกันสำหรับทีม MotionReach และ Brandstorm — ลงทะเบียนซัพพลายเออร์ ตรวจใบแจ้งหนี้ และจัดการการจัดซื้อให้เป็นระเบียบ',
  'Submit invoices, track every approval step, and get paid faster. We pay partners on real terms — and tell you exactly when.':
    'ส่งใบแจ้งหนี้ ติดตามทุกขั้นตอนการอนุมัติ และรับเงินเร็วขึ้น เราจ่ายพาร์ทเนอร์ตามเงื่อนไขจริง — และบอกคุณชัดเจนว่าเมื่อไร',
  "Enter the email on file with MotionReach × Brandstorm and we'll send a secure reset link, valid for 30 minutes.":
    'กรอกอีเมลที่บันทึกไว้กับ MotionReach × Brandstorm แล้วเราจะส่งลิงก์รีเซ็ตที่ปลอดภัย ใช้ได้ภายใน 30 นาที',
  "Choose a password for your MotionReach vendor account. You'll use your email and this password to sign in from now on.":
    'เลือกรหัสผ่านสำหรับบัญชีซัพพลายเออร์ MotionReach ของคุณ จากนี้ไปคุณจะใช้อีเมลและรหัสผ่านนี้เข้าสู่ระบบ',

  // ---- Notifications & settings menus ----
  '{n} unread · synced just now': '{n} ยังไม่อ่าน · ซิงค์เมื่อสักครู่',
  'Mark all read': 'ทำเครื่องหมายอ่านทั้งหมด',
  'Unread': 'ยังไม่อ่าน',
  'TODAY': 'วันนี้',
  'YESTERDAY': 'เมื่อวาน',
  'EARLIER THIS WEEK': 'ก่อนหน้านี้ในสัปดาห์นี้',
  'View all in Audit log': 'ดูทั้งหมดในบันทึกการตรวจสอบ',
  'Appearance': 'การแสดงผล',
  'Light': 'สว่าง',
  'Dark': 'มืด',
  'Density': 'ความหนาแน่น',
  'Compact': 'กระชับ',
  'Comfy': 'สบายตา',
  'Accent': 'สีเน้น',
  'Keyboard shortcuts': 'ปุ่มลัดคีย์บอร์ด',
  'Add teammate': 'เพิ่มสมาชิกทีม',
  'Portal': 'พอร์ทัล',
  'Workspace': 'พื้นที่ทำงาน',

  // ---- Modals: invite teammate ----
  'Add team member': 'เพิ่มสมาชิกทีม',
  'Adds a row to the Users tab so this Google account can sign in to the staff app.':
    'เพิ่มแถวในแท็บ Users เพื่อให้บัญชี Google นี้เข้าสู่ระบบแอปของทีมงานได้',
  'Email address': 'ที่อยู่อีเมล',
  "Must match the Google account they'll sign in with.": 'ต้องตรงกับบัญชี Google ที่พวกเขาจะใช้เข้าสู่ระบบ',
  'Their display name': 'ชื่อที่แสดงของพวกเขา',
  'Permission role': 'บทบาทสิทธิ์',
  'Project Manager': 'ผู้จัดการโปรเจกต์',
  'Full access — vendors, invoices, billing, team': 'เข้าถึงเต็มรูปแบบ — ซัพพลายเออร์ ใบแจ้งหนี้ การวางบิล ทีม',
  'Approve invoices, register vendors, send portal links': 'อนุมัติใบแจ้งหนี้ ลงทะเบียนซัพพลายเออร์ ส่งลิงก์พอร์ทัล',
  'Verify invoices, schedule payments, manage compliance': 'ตรวจใบแจ้งหนี้ กำหนดการจ่าย จัดการการปฏิบัติตามข้อกำหนด',
  'Read-only access to vendors and invoices': 'เข้าถึงแบบอ่านอย่างเดียวสำหรับซัพพลายเออร์และใบแจ้งหนี้',
  'All actions': 'ทุกการกระทำ',
  'Approve invoices': 'อนุมัติใบแจ้งหนี้',
  'Vendor registration': 'ลงทะเบียนซัพพลายเออร์',
  'Send portal links': 'ส่งลิงก์พอร์ทัล',
  'Finance verify': 'การเงินตรวจสอบ',
  'Pay runs': 'รอบการจ่ายเงิน',
  'Banking': 'ข้อมูลธนาคาร',
  'Read vendors': 'อ่านข้อมูลซัพพลายเออร์',
  'Read invoices': 'อ่านใบแจ้งหนี้',
  'Adding…': 'กำลังเพิ่ม…',
  'Add to team': 'เพิ่มเข้าทีม',

  // ---- Modals: keyboard shortcuts ----
  'Move through the system without taking your hands off the keys.': 'ใช้งานระบบได้โดยไม่ต้องละมือจากคีย์บอร์ด',
  'Navigation': 'การนำทาง',
  'Suppliers list': 'รายการซัพพลายเออร์',
  'Invoice review': 'การตรวจใบแจ้งหนี้',
  'Theme & view': 'ธีมและมุมมอง',
  'Go to Suppliers': 'ไปที่ซัพพลายเออร์',
  'Go to Approval queue': 'ไปที่คิวอนุมัติ',
  'Go to Billing inbox': 'ไปที่กล่องวางบิล',
  'Go to Reports': 'ไปที่รายงาน',
  'Open command bar (search anything)': 'เปิดแถบคำสั่ง (ค้นหาทุกอย่าง)',
  'Show this shortcuts panel': 'แสดงแผงปุ่มลัดนี้',
  'Toggle sidebar': 'สลับแถบด้านข้าง',
  'Focus filter': 'โฟกัสตัวกรอง',
  'Select range': 'เลือกช่วง',
  'Toggle row': 'สลับแถว',
  'Open selected supplier': 'เปิดซัพพลายเออร์ที่เลือก',
  'Export current view': 'ส่งออกมุมมองปัจจุบัน',
  'Request more info': 'ขอข้อมูลเพิ่มเติม',
  'Next / previous invoice': 'ใบแจ้งหนี้ถัดไป / ก่อนหน้า',
  'Approve and move to next': 'อนุมัติแล้วไปรายการถัดไป',
  'Toggle light/dark theme': 'สลับธีมสว่าง/มืด',
  'Toggle density': 'สลับความหนาแน่น',
  'Hint: press': 'คำแนะนำ: กด',
  'from anywhere': 'จากที่ใดก็ได้',
  'Got it': 'เข้าใจแล้ว',

  // ---- Modals: pay run ----
  '{a} of {b} approved invoices selected': 'เลือกแล้ว {a} จาก {b} ใบแจ้งหนี้ที่อนุมัติ',
  'total': 'รวม',
  'Approved invoices ready to schedule': 'ใบแจ้งหนี้ที่อนุมัติแล้วพร้อมกำหนดจ่าย',
  'Deselect all': 'ยกเลิกเลือกทั้งหมด',
  'Select all': 'เลือกทั้งหมด',
  'No invoices in approved-payment yet. Approve invoices in the Billing inbox to make them schedulable here.':
    'ยังไม่มีใบแจ้งหนี้ในสถานะ "อนุมัติจ่ายแล้ว" อนุมัติใบแจ้งหนี้ในกล่องวางบิลเพื่อให้กำหนดจ่ายได้ที่นี่',
  'Total to disburse': 'ยอดรวมที่จะจ่าย',
  'Across {a} invoices · {b} vendors': '{a} ใบแจ้งหนี้ · {b} ซัพพลายเออร์',
  'Pay-out date': 'วันที่จ่ายเงิน',
  "Written to each invoice's payment_scheduled column.": 'บันทึกในคอลัมน์ payment_scheduled ของแต่ละใบแจ้งหนี้',
  'Vendors will be notified the moment the run is signed off. Any disputed invoices stay excluded until resolved.':
    'ซัพพลายเออร์จะได้รับแจ้งทันทีที่รอบการจ่ายได้รับการอนุมัติ ใบแจ้งหนี้ที่มีข้อโต้แย้งจะถูกกันออกจนกว่าจะแก้ไขเสร็จ',
  'across {n} invoices': '{n} ใบแจ้งหนี้',
  'Scheduling…': 'กำลังกำหนด…',
  'Schedule {n} payments': 'กำหนดจ่าย {n} รายการ',

  // ---- Supplier detail (internal) ----
  'Overview': 'ภาพรวม',
  'Past projects': 'โปรเจกต์ที่ผ่านมา',
  'Billing history': 'ประวัติการวางบิล',
  'Projects (12 mo)': 'โปรเจกต์ (12 เดือน)',
  'Quality score': 'คะแนนคุณภาพ',
  'Company & banking': 'ข้อมูลบริษัทและธนาคาร',
  'City / Country': 'เมือง / ประเทศ',
  'unassigned': 'ยังไม่ได้กำหนด',
  'Added {date}': 'เพิ่มเมื่อ {date}',
  '{n} paid · {year}': 'จ่ายแล้ว {n} ใบ · {year}',
  '{n} active': 'ใช้งานอยู่ {n}',
  '{n} paid invoices': 'ใบแจ้งหนี้ที่จ่ายแล้ว {n} ใบ',
  '{n} reviews': 'รีวิว {n} รายการ',
  'Review the KYC documents on the Documents tab, then verify': 'ตรวจเอกสาร KYC ในแท็บเอกสาร แล้วจึงยืนยัน',
  'Copy a KYC-upload link and email it to the supplier': 'คัดลอกลิงก์อัปโหลด KYC แล้วส่งอีเมลให้ซัพพลายเออร์',

  // ---- Suppliers list (internal toolbar / table) ----
  'Loading suppliers…': 'กำลังโหลดซัพพลายเออร์…',
  'No suppliers match your filters.': 'ไม่มีซัพพลายเออร์ที่ตรงกับตัวกรอง',
  'Show less': 'แสดงน้อยลง',
  '+{n} more': '+อีก {n}',
  'Any terms': 'เงื่อนไขทั้งหมด',
  'Any rating': 'คะแนนทั้งหมด',
  '3.0 and up': '3.0 ขึ้นไป',
  '4.0 and up': '4.0 ขึ้นไป',
  '4.5 and up': '4.5 ขึ้นไป',
  'Recently updated': 'อัปเดตล่าสุด',
  'Name (A–Z)': 'ชื่อ (A–Z)',
  'Rating (high→low)': 'คะแนน (สูง→ต่ำ)',
  'Spend (high→low)': 'ยอดใช้จ่าย (สูง→ต่ำ)',
  'Projects (high→low)': 'โปรเจกต์ (มาก→น้อย)',
  'Comfortable': 'สบายตา',
  'Status: {v}': 'สถานะ: {v}',
  'Terms: any': 'เงื่อนไข: ทั้งหมด',
  'Terms: {v}': 'เงื่อนไข: {v}',
  'Rating {v}': 'คะแนน {v}',
  'Rating: any': 'คะแนน: ทั้งหมด',
  'Search suppliers…': 'ค้นหาซัพพลายเออร์…',
  '{a} of {b} rows': '{a} จาก {b} แถว',
  'Sort by {c}': 'เรียงตาม {c}',
  'Sum spend: {v}': 'ยอดใช้จ่ายรวม: {v}',
  'Avg rating: {v}': 'คะแนนเฉลี่ย: {v}',

  // ---- Supplier detail sub-tabs (docs / projects / billing / audit) ----
  '{n} documents': 'เอกสาร {n} รายการ',
  'Document': 'เอกสาร',
  'Type': 'ประเภท',
  'Uploaded': 'อัปโหลดเมื่อ',
  'Size': 'ขนาด',
  'KYC / compliance': 'KYC / การปฏิบัติตามข้อกำหนด',
  'Invoice PDF': 'PDF ใบแจ้งหนี้',
  'Total projects': 'โปรเจกต์ทั้งหมด',
  '{a} active · {b} closed': 'ใช้งานอยู่ {a} · ปิดแล้ว {b}',
  'Lifetime value': 'มูลค่าสะสม',
  'no reviews yet': 'ยังไม่มีรีวิว',
  'avg {v}/5': 'เฉลี่ย {v}/5',
  'No project activity yet. Projects appear here when the supplier submits invoices.':
    'ยังไม่มีกิจกรรมโปรเจกต์ โปรเจกต์จะปรากฏที่นี่เมื่อซัพพลายเออร์ส่งใบแจ้งหนี้',
  'Period': 'ช่วงเวลา',
  'Lines': 'รายการ',
  'Contract value': 'มูลค่าสัญญา',
  'Quality': 'คุณภาพ',
  'Closed': 'ปิดแล้ว',
  'not yet rated': 'ยังไม่ได้ให้คะแนน',
  'Invoices': 'ใบแจ้งหนี้',
  'lifetime': 'สะสม',
  'Paid total': 'ยอดจ่ายรวม',
  'Outstanding': 'ค้างชำระ',
  '{a} in review · {b} scheduled': 'อยู่ระหว่างตรวจสอบ {a} · กำหนดจ่าย {b}',
  'Avg cycle time': 'รอบเวลาเฉลี่ย',
  '{n} samples': 'ตัวอย่าง {n}',
  'No invoices on file yet.': 'ยังไม่มีใบแจ้งหนี้ในระบบ',
  'Cycle (d)': 'รอบ (วัน)',
  'No audit events recorded for this supplier yet.': 'ยังไม่มีบันทึกการตรวจสอบสำหรับซัพพลายเออร์รายนี้',
  'When': 'เมื่อ',
  'Actor': 'ผู้กระทำ',
  'Action': 'การกระทำ',
  'Entity': 'รายการ',

  // ---- Approval queue (detail panel) ----
  'No billing documents are awaiting approval. Invoice review happens in the Billing inbox.':
    'ไม่มีเอกสารวางบิลที่รออนุมัติ การตรวจใบแจ้งหนี้ทำในกล่องวางบิล',
  'No supplier profile-change requests are pending review.':
    'ไม่มีคำขอแก้ไขข้อมูลซัพพลายเออร์ที่รอตรวจสอบ',
  'No disputed items right now. Disputed invoices are managed in the Billing inbox.':
    'ขณะนี้ไม่มีรายการที่มีข้อโต้แย้ง ใบแจ้งหนี้ที่มีข้อโต้แย้งจัดการในกล่องวางบิล',
  'No pending approvals. Register a new supplier to see one here.':
    'ไม่มีรายการรออนุมัติ ลงทะเบียนซัพพลายเออร์ใหม่เพื่อแสดงที่นี่',
  'Submitted by {who} ({role}) · {when} · {n} docs': 'ส่งโดย {who} ({role}) · {when} · {n} เอกสาร',
  'Submitted by': 'ส่งโดย',
  'Proposed terms': 'เงื่อนไขที่เสนอ',
  'Submission note': 'หมายเหตุการส่ง',
  'National ID format valid (13 digits)': 'รูปแบบเลขบัตรประชาชนถูกต้อง (13 หลัก)',
  'Tax ID format valid (13 digits)': 'รูปแบบเลขผู้เสียภาษีถูกต้อง (13 หลัก)',
  'Bank account name on file': 'มีชื่อบัญชีธนาคารในระบบ',
  'Bank account number on file': 'มีเลขบัญชีธนาคารในระบบ',
  'Credit limit set': 'กำหนดวงเงินเครดิตแล้ว',
  'Contact email present': 'มีอีเมลผู้ติดต่อ',
  'National ID copy uploaded': 'อัปโหลดสำเนาบัตรประชาชนแล้ว',
  'Bank book uploaded': 'อัปโหลดสมุดบัญชีธนาคารแล้ว',
  'Business registration uploaded': 'อัปโหลดหนังสือรับรองบริษัทแล้ว',
  'KYC documents ({n})': 'เอกสาร KYC ({n})',
  'No KYC documents on file for this vendor.': 'ไม่มีเอกสาร KYC ในระบบสำหรับซัพพลายเออร์รายนี้',
  'On file': 'มีในระบบ',
  'Add context, rejection reason, or follow-ups…': 'เพิ่มบริบท เหตุผลการปฏิเสธ หรือสิ่งที่ต้องติดตาม…',
  'Rejecting…': 'กำลังปฏิเสธ…',
  'Approving…': 'กำลังอนุมัติ…',
  'KYC document': 'เอกสาร KYC',
  'Contract': 'สัญญา',
  'NDA': 'สัญญารักษาความลับ (NDA)',
  'Withholding tax': 'ภาษีหัก ณ ที่จ่าย',

  // ---- Quality reviews ----
  'Filters': 'ตัวกรอง',
  'Reviews submitted': 'รีวิวที่ส่งแล้ว',
  'across all engagements': 'จากทุกงาน',
  'Average score': 'คะแนนเฉลี่ย',
  '{n} reviews scored': 'ให้คะแนนแล้ว {n} รีวิว',
  'Suppliers reviewed': 'ซัพพลายเออร์ที่ถูกรีวิว',
  '{n}% coverage': 'ครอบคลุม {n}%',
  'Flagged for review': 'ถูกตั้งสถานะให้ตรวจสอบ',
  'Score < 3.0': 'คะแนน < 3.0',
  'Open review — {name}': 'รีวิวที่เปิดอยู่ — {name}',
  'No reviews': 'ไม่มีรีวิว',
  'Matching the current filters': 'ตรงกับตัวกรองปัจจุบัน',
  'No reviews match these filters.': 'ไม่มีรีวิวที่ตรงกับตัวกรอง',
  'Preferred supplier': 'ซัพพลายเออร์แนะนำ',
  'Submitted by {who}': 'ส่งโดย {who}',
  'Quality of work': 'คุณภาพงาน',
  'Timeliness': 'ความตรงต่อเวลา',
  'Communication': 'การสื่อสาร',
  'Value for money': 'ความคุ้มค่า',
  'PM comment': 'ความเห็นจาก PM',
  'No comment left for this review.': 'ไม่มีความเห็นสำหรับรีวิวนี้',
  'Recent reviews': 'รีวิวล่าสุด',
  'Score': 'คะแนน',
  'Reviewer': 'ผู้รีวิว',
  'Date': 'วันที่',
  'Flagged — score < 3.0': 'ถูกตั้งสถานะ — คะแนน < 3.0',
  'Live from the Vendors master · {n} vendors': 'ดึงสดจากฐานข้อมูลซัพพลายเออร์ · {n} ราย',
  'No flagged vendors. All active suppliers currently rated 3.0 or above.':
    'ไม่มีซัพพลายเออร์ที่ถูกตั้งสถานะ ซัพพลายเออร์ที่ใช้งานอยู่ทั้งหมดมีคะแนน 3.0 ขึ้นไป',
  '{n} projects': 'โปรเจกต์ {n}',

  // ---- Billing inbox (internal) ----
  'All supplier invoices in motion': 'ใบแจ้งหนี้ซัพพลายเออร์ทั้งหมดที่กำลังดำเนินการ',
  'No invoices in the queue yet. They\'ll appear here as suppliers submit them from the portal.':
    'ยังไม่มีใบแจ้งหนี้ในคิว จะปรากฏที่นี่เมื่อซัพพลายเออร์ส่งจากพอร์ทัล',
  '{n} invoices · avg 11h old': '{n} ใบแจ้งหนี้ · เฉลี่ยอายุ 11 ชม.',
  '{n} invoices · 1 high-value': '{n} ใบแจ้งหนี้ · มูลค่าสูง 1 ใบ',
  'Next pay run: Wed 21 May': 'รอบจ่ายถัดไป: พุธ 21 พ.ค.',
  '{n} need follow-up': 'ต้องติดตาม {n} รายการ',
  'Project · PO': 'โปรเจกต์ · PO',
  'Owner': 'ผู้รับผิดชอบ',
  'Age': 'อายุ',
  'Scheduled pay': 'วันกำหนดจ่าย',
  'Payment date has passed': 'เลยวันกำหนดจ่ายแล้ว',
  'From': 'ตั้งแต่',
  'To': 'ถึง',
  'Hide panel': 'ซ่อนแผง',
  'Hide panel (Esc)': 'ซ่อนแผง (Esc)',
  'Clear filters': 'ล้างตัวกรอง',
  'Vendor': 'ซัพพลายเออร์',
  'Project': 'โปรเจกต์',
  'Amount': 'จำนวนเงิน',
  'All vendors': 'ซัพพลายเออร์ทั้งหมด',
  'All projects': 'โปรเจกต์ทั้งหมด',
  'All owners': 'ผู้รับผิดชอบทั้งหมด',
  'Any amount': 'จำนวนเงินทั้งหมด',
  'Under ฿50,000': 'น้อยกว่า ฿50,000',
  '฿50,000 – ฿200,000': '฿50,000 – ฿200,000',
  'Over ฿200,000': 'มากกว่า ฿200,000',
  'Search invoice, PO, vendor…': 'ค้นหาใบแจ้งหนี้ PO ซัพพลายเออร์…',
  'Due date': 'วันครบกำหนด',
  'Inclusive of 7% VAT · WHT 3% withheld': 'รวม VAT 7% · หัก ณ ที่จ่าย 3%',
  'Submission checklist': 'รายการตรวจสอบการส่ง',
  'PO number provided': 'ระบุเลขที่ PO แล้ว',
  'Invoice document attached': 'แนบเอกสารใบแจ้งหนี้แล้ว',
  'Proof of performance attached': 'แนบหลักฐานการส่งมอบงานแล้ว',
  'Amount is positive': 'จำนวนเงินมากกว่าศูนย์',
  'Invoice date provided': 'ระบุวันที่ในใบแจ้งหนี้แล้ว',
  'VAT recorded': 'บันทึก VAT แล้ว',
  'Flagging…': 'กำลังตั้งสถานะ…',
  'Working…': 'กำลังดำเนินการ…',
  'Download is available to MotionReach staff. To re-send a copy, email your MotionReach contact.':
    'การดาวน์โหลดทำได้เฉพาะทีมงาน MotionReach หากต้องการสำเนาใหม่ โปรดอีเมลผู้ติดต่อ MotionReach ของคุณ',
  'No compliance documents on file yet.': 'ยังไม่มีเอกสารการปฏิบัติตามข้อกำหนดในระบบ',
  'Issued': 'ออกเมื่อ',
  'Expires': 'หมดอายุ',

  // ---- Audit log (internal) ----
  '{n} events': '{n} เหตุการณ์',
  'State change': 'การเปลี่ยนสถานะ',
  'Note': 'หมายเหตุ',
  'Timestamp': 'เวลาบันทึก',
  'Session': 'เซสชัน',
  'Approved': 'อนุมัติแล้ว',
  'Created': 'สร้างแล้ว',
  'Edited': 'แก้ไขแล้ว',
  'Viewed': 'ดูแล้ว',
  'Uploaded': 'อัปโหลดแล้ว',
  'Signed in': 'เข้าสู่ระบบแล้ว',

  // ---- Reports (internal) ----
  'Total spend (vendor master)': 'ยอดใช้จ่ายรวม (ฐานข้อมูลซัพพลายเออร์)',
  'across {n} vendors': '{n} ซัพพลายเออร์',
  'Active vendors': 'ซัพพลายเออร์ที่ใช้งาน',
  'of {n} total': 'จากทั้งหมด {n}',
  'Avg days to pay': 'จำนวนวันเฉลี่ยที่จ่าย',
  'across {n} paid invoices': 'ใบแจ้งหนี้ที่จ่ายแล้ว {n} ใบ',
  'Invoices in flight': 'ใบแจ้งหนี้ที่กำลังดำเนินการ',
  '{a} paid · {b} flagged': 'จ่ายแล้ว {a} · ตั้งสถานะ {b}',
  'Dispute rate': 'อัตราข้อโต้แย้ง',
  '{a} of {b} invoices': '{a} จาก {b} ใบแจ้งหนี้',
  'Monthly spend': 'ยอดใช้จ่ายรายเดือน',
  'Trailing 12 months · THB': 'ย้อนหลัง 12 เดือน · บาท',
  'No paid invoices yet — monthly spend populates as payments complete.':
    'ยังไม่มีใบแจ้งหนี้ที่จ่ายแล้ว — ยอดใช้จ่ายรายเดือนจะแสดงเมื่อมีการจ่ายเงิน',
  'This month': 'เดือนนี้',
  'vs prior month': 'เทียบเดือนก่อน',
  'Peak month': 'เดือนสูงสุด',
  '12-mo total': 'รวม 12 เดือน',
  'Spend by category': 'ยอดใช้จ่ายตามหมวดหมู่',
  'Top vendors by spend': 'ซัพพลายเออร์ที่ใช้จ่ายสูงสุด',
  'Trend': 'แนวโน้ม',
  'Share': 'สัดส่วน',
  'Payment cycle time': 'รอบเวลาการจ่ายเงิน',
  'Days from invoice submission to paid': 'จำนวนวันจากการส่งใบแจ้งหนี้จนถึงจ่ายเงิน',
  'No paid invoices yet — cycle times appear once invoices are paid.':
    'ยังไม่มีใบแจ้งหนี้ที่จ่ายแล้ว — รอบเวลาจะแสดงเมื่อมีการจ่ายใบแจ้งหนี้',
  'Fastest stage': 'ขั้นตอนเร็วสุด',
  'Slowest stage': 'ขั้นตอนช้าสุด',
  'Avg total': 'เฉลี่ยรวม',
  'Performance — by category': 'ประสิทธิภาพ — ตามหมวดหมู่',
  'Vendors': 'ซัพพลายเออร์',
  'Spend': 'ยอดใช้จ่าย',
  'Avg quality': 'คุณภาพเฉลี่ย',
  'On-time %': 'ตรงเวลา %',
  'Avg cycle (d)': 'รอบเฉลี่ย (วัน)',

  // ---- App shell: modals / dashboard / misc ----
  'Backend error.': 'ข้อผิดพลาดของระบบหลังบ้าน',
  'Rate & approve': 'ให้คะแนนและอนุมัติ',
  'Required before PM approval · {name}': 'จำเป็นก่อน PM อนุมัติ · {name}',
  'Project: {project} · Invoice: {invoice}': 'โปรเจกต์: {project} · ใบแจ้งหนี้: {invoice}',
  'Comment for the supplier': 'ความเห็นถึงซัพพลายเออร์',
  'What went well, what could improve…': 'อะไรที่ดี อะไรที่ควรปรับปรุง…',
  'Internal note for finance': 'หมายเหตุภายในถึงฝ่ายการเงิน',
  'Any flag for the next reviewer…': 'มีอะไรต้องแจ้งผู้ตรวจสอบคนถัดไปไหม…',
  'Submit rating & approve': 'ส่งคะแนนและอนุมัติ',
  'Submitting…': 'กำลังส่ง…',
  // 2026-09-16 — upload progress + slow-server strip
  'Uploading {n} of {total}…': 'กำลังอัปโหลดไฟล์ {n} จาก {total}…',
  'Waking up the server…': 'กำลังปลุกเซิร์ฟเวอร์…',
  "The first request after a quiet spell can take up to 30 seconds. Please don't re-click.": 'คำขอแรกหลังจากไม่มีการใช้งานสักพักอาจใช้เวลาถึง 30 วินาที กรุณาอย่ากดซ้ำ',
  'New quality review': 'รีวิวคุณภาพใหม่',
  'Score a supplier at project closure': 'ให้คะแนนซัพพลายเออร์เมื่อปิดโปรเจกต์',
  '— Select supplier —': '— เลือกซัพพลายเออร์ —',
  'Comment': 'ความเห็น',
  'Submit review': 'ส่งรีวิว',
  'Edit supplier profile': 'แก้ไขข้อมูลซัพพลายเออร์',
  'Notes': 'หมายเหตุ',
  'Assigned PM': 'PM ที่ดูแล',
  'Routes PM-review invoices for this supplier · admin-only':
    'กำหนดเส้นทางใบแจ้งหนี้รอ PM ตรวจสำหรับซัพพลายเออร์นี้ · เฉพาะผู้ดูแลระบบ',
  '— Unassigned (any PM can review) —': '— ยังไม่กำหนด (PM คนใดก็ตรวจได้) —',
  'Search suppliers, invoices, screens…': 'ค้นหาซัพพลายเออร์ ใบแจ้งหนี้ หน้าจอ…',
  'Screens': 'หน้าจอ',
  'Suppliers ({n})': 'ซัพพลายเออร์ ({n})',
  'Invoices ({n})': 'ใบแจ้งหนี้ ({n})',
  'No results.': 'ไม่พบผลลัพธ์',
  'Enter to pick · Esc to close': 'Enter เพื่อเลือก · Esc เพื่อปิด',
  '{a} vendors · {b} invoices': 'ซัพพลายเออร์ {a} · ใบแจ้งหนี้ {b}',
  'Needs your attention': 'รายการที่ต้องดำเนินการ',
  '{n} items': '{n} รายการ',
  'You\'re all caught up — nothing rejected or disputed.': 'เรียบร้อยแล้ว — ไม่มีรายการที่ถูกปฏิเสธหรือโต้แย้ง',
  'Open': 'เปิด',
  'Upcoming payments': 'การจ่ายที่กำลังจะถึง',
  'No payments scheduled yet.': 'ยังไม่มีการกำหนดจ่ายเงิน',
  'pay by {date}': 'จ่ายภายใน {date}',
  'submitted {age}': 'ส่งเมื่อ {age}',
  'Add context for the next reviewer, or explain the dispute…': 'เพิ่มบริบทสำหรับผู้ตรวจสอบคนถัดไป หรืออธิบายข้อโต้แย้ง…',
  'On approval this invoice is written to the {company} Account Payable sheet (net of WHT, status “awaiting payment”).':
    'เมื่ออนุมัติ ใบแจ้งหนี้นี้จะถูกบันทึกลงชีต Account Payable ของ {company} (หักภาษี ณ ที่จ่ายแล้ว สถานะ “รอจ่าย”)',
  'Required — picks which AP sheet this approved invoice is posted to.':
    'จำเป็น — เลือกว่าจะบันทึกใบแจ้งหนี้ที่อนุมัติแล้วลงชีต AP ใด',
  'Export CSV': 'ส่งออก CSV',
  'Submit rating & approve': 'ส่งคะแนนและอนุมัติ',
  'Portal dashboard': 'แดชบอร์ดพอร์ทัล',

  // ---- Settings: account + notifications (portal) ----
  'Company': 'บริษัท',
  'Invoice lifecycle notifications': 'การแจ้งเตือนตามขั้นตอนใบแจ้งหนี้',
  'Pick channels per event': 'เลือกช่องทางสำหรับแต่ละเหตุการณ์',
  'Event': 'เหตุการณ์',
  'Description': 'คำอธิบาย',
  'In-app': 'ในแอป',
  'always': 'เสมอ',
  'Invoice submitted': 'ส่งใบแจ้งหนี้แล้ว',
  'When we receive your invoice from the portal': 'เมื่อเราได้รับใบแจ้งหนี้ของคุณจากพอร์ทัล',
  'PM review complete': 'PM ตรวจสอบเสร็จ',
  'When the project manager approves or returns the invoice': 'เมื่อผู้จัดการโปรเจกต์อนุมัติหรือส่งใบแจ้งหนี้กลับ',
  'Finance verification': 'การตรวจสอบของฝ่ายการเงิน',
  'When Finance starts and finishes verification': 'เมื่อฝ่ายการเงินเริ่มและเสร็จสิ้นการตรวจสอบ',
  'When your invoice is queued for the next pay run': 'เมื่อใบแจ้งหนี้ของคุณเข้าคิวรอบจ่ายถัดไป',
  'Payment released': 'จ่ายเงินแล้ว',
  'When funds are transferred to your bank account': 'เมื่อมีการโอนเงินเข้าบัญชีธนาคารของคุณ',
  'Disputed or returned': 'มีข้อโต้แย้งหรือถูกส่งกลับ',
  "When there's an issue we need your input on": 'เมื่อมีปัญหาที่ต้องการข้อมูลจากคุณ',
  'Weekly statement': 'สรุปประจำสัปดาห์',
  'Friday summary of all activity': 'สรุปกิจกรรมทั้งหมดทุกวันศุกร์',

  // ---- Settings: team access (portal) ----
  'Team members': 'สมาชิกทีม',
  'Invite teammate': 'เชิญสมาชิกทีม',
  'Role': 'บทบาท',
  'Member — submit & track invoices': 'สมาชิก — ส่งและติดตามใบแจ้งหนี้',
  'Owner — also manage the team': 'เจ้าของ — จัดการทีมได้ด้วย',
  'Sending…': 'กำลังส่ง…',
  'Send invite': 'ส่งคำเชิญ',
  "They'll get an email with a link to set their own password. Their login is tied to {company}.":
    'พวกเขาจะได้รับอีเมลพร้อมลิงก์เพื่อตั้งรหัสผ่านของตนเอง บัญชีเข้าสู่ระบบจะผูกกับ {company}',
  'Loading team…': 'กำลังโหลดทีม…',
  'Member': 'สมาชิก',
  'Last active': 'ใช้งานล่าสุด',
  '(you)': '(คุณ)',
  'Invited {date}': 'เชิญเมื่อ {date}',
  'Restore': 'คืนสิทธิ์',
  'Pause': 'ระงับ',
  'Remove': 'ลบ',
  'Only team owners can add or remove members. Ask an owner at your company to invite a colleague.':
    'เฉพาะเจ้าของทีมเท่านั้นที่เพิ่มหรือลบสมาชิกได้ ขอให้เจ้าของทีมในบริษัทของคุณช่วยเชิญเพื่อนร่วมงาน',
  'your company': 'บริษัทของคุณ',

  // ---- Notification activity verbs (bell menu) ----
  'submitted a new invoice': 'ส่งใบแจ้งหนี้ใหม่',
  'PM-approved an invoice': 'PM อนุมัติใบแจ้งหนี้',
  'approved an invoice for payment': 'อนุมัติใบแจ้งหนี้เพื่อจ่าย',
  'scheduled a payment': 'กำหนดการจ่ายเงิน',
  'marked an invoice paid': 'ทำเครื่องหมายว่าจ่ายใบแจ้งหนี้แล้ว',
  'rejected an invoice': 'ปฏิเสธใบแจ้งหนี้',
  'registered a new vendor': 'ลงทะเบียนซัพพลายเออร์ใหม่',
  'approved a vendor': 'อนุมัติซัพพลายเออร์',
  'rejected a vendor': 'ปฏิเสธซัพพลายเออร์',
  'submitted a quality review': 'ส่งรีวิวคุณภาพ',
  'issued a portal link': 'สร้างลิงก์พอร์ทัล',
  'added a team member': 'เพิ่มสมาชิกทีม',
  'approved your invoice': 'อนุมัติใบแจ้งหนี้ของคุณ',
  'submitted a new vendor': 'ส่งซัพพลายเออร์ใหม่',
  'flagged for quality review': 'ตั้งสถานะเพื่อตรวจสอบคุณภาพ',
  'released payment': 'จ่ายเงินแล้ว',
  'document expiring soon': 'เอกสารกำลังจะหมดอายุ',

  // ---- Supplier archive / delete (2026-06-17) ----
  'Archive': 'เก็บเข้าคลัง',
  'Unarchive': 'นำออกจากคลัง',
  'Delete': 'ลบ',
  'Archived': 'เก็บเข้าคลังแล้ว',
  'Confirm': 'ยืนยัน',
  'Delete supplier?': 'ลบซัพพลายเออร์?',
  'Restore supplier?': 'คืนค่าซัพพลายเออร์?',
  'Archive supplier?': 'เก็บซัพพลายเออร์เข้าคลัง?',
  'Delete permanently': 'ลบถาวร',
  'Hide this supplier from the list. Reversible — keeps all history.':
    'ซ่อนซัพพลายเออร์นี้จากรายการ ย้อนกลับได้ — เก็บประวัติทั้งหมดไว้',
  'This supplier has invoices — archive it instead to preserve finance history.':
    'ซัพพลายเออร์นี้มีใบแจ้งหนี้ — ให้เก็บเข้าคลังแทนเพื่อรักษาประวัติทางการเงิน',
  'Permanently delete this supplier (no invoices on file).':
    'ลบซัพพลายเออร์นี้อย่างถาวร (ไม่มีใบแจ้งหนี้ในระบบ)',
  'This permanently removes the supplier and its logins, tokens and KYC documents. It cannot be undone. (Suppliers with invoices cannot be deleted — archive them instead.)':
    'การดำเนินการนี้จะลบซัพพลายเออร์รวมถึงบัญชีเข้าสู่ระบบ โทเค็น และเอกสาร KYC อย่างถาวร ไม่สามารถย้อนกลับได้ (ซัพพลายเออร์ที่มีใบแจ้งหนี้จะลบไม่ได้ — ให้เก็บเข้าคลังแทน)',
  'This restores the supplier to the active Suppliers list.':
    'การดำเนินการนี้จะคืนซัพพลายเออร์กลับสู่รายการที่ใช้งานอยู่',
  'This hides the supplier from the default list. Nothing is deleted — you can restore it anytime from the Archived filter.':
    'การดำเนินการนี้จะซ่อนซัพพลายเออร์จากรายการเริ่มต้น ไม่มีการลบข้อมูล — คุณคืนค่าได้ทุกเมื่อจากตัวกรองเก็บเข้าคลัง',

  // ---- Invoice edit + payment date (2026-06-17) ----
  'Edit': 'แก้ไข',
  'Edit details': 'แก้ไขรายละเอียด',
  'Edit invoice details': 'แก้ไขรายละเอียดใบแจ้งหนี้',
  'Invoice updated': 'อัปเดตใบแจ้งหนี้แล้ว',
  'Amount (Ex-VAT) THB': 'จำนวนเงิน (ก่อน VAT) บาท',
  'VAT THB': 'VAT บาท',
  'Withholding tax THB': 'ภาษีหัก ณ ที่จ่าย บาท',
  'Expense category · Account Payable': 'หมวดค่าใช้จ่าย · เจ้าหนี้การค้า',
  'Keep current category': 'คงหมวดเดิมไว้',
  'You can still edit this invoice until MotionReach starts reviewing it.':
    'คุณยังแก้ไขใบแจ้งหนี้นี้ได้จนกว่า MotionReach จะเริ่มตรวจสอบ',
  'This invoice is already in the {company} Account Payable sheet — your changes are synced to that row.':
    'ใบแจ้งหนี้นี้อยู่ในชีตเจ้าหนี้การค้าของ {company} แล้ว — การเปลี่ยนแปลงของคุณจะซิงค์ไปยังแถวนั้น',
  'Payment date': 'วันที่จ่ายเงิน',
  "Written to the Account Payable sheet's Due Date column when you mark this scheduled.":
    'จะถูกบันทึกลงในคอลัมน์วันครบกำหนดของชีตเจ้าหนี้การค้าเมื่อคุณทำเครื่องหมายว่ากำหนดจ่ายแล้ว',
  "Written to each invoice and to the Account Payable sheet's Due Date column.":
    'บันทึกลงในแต่ละใบแจ้งหนี้และคอลัมน์วันครบกำหนดของชีตเจ้าหนี้การค้า',

  // ---- Save draft (browser-local form drafts) ----
  'Save draft': 'บันทึกฉบับร่าง',
  'Draft saved': 'บันทึกฉบับร่างแล้ว',
  'Draft saved · {when}': 'บันทึกฉบับร่างแล้ว · {when}',
  'Resume your saved draft?': 'ดำเนินการต่อจากฉบับร่างที่บันทึกไว้?',
  "Saved {when}. Restore to keep editing, or discard to start fresh. Uploaded files aren't part of a draft — re-attach them.":
    'บันทึกเมื่อ {when} · เรียกคืนเพื่อแก้ไขต่อ หรือละทิ้งเพื่อเริ่มใหม่ ไฟล์ที่อัปโหลดไม่รวมอยู่ในฉบับร่าง — โปรดแนบใหม่อีกครั้ง',
  'Restore draft': 'เรียกคืนฉบับร่าง',
  'Discard': 'ละทิ้ง',
  'just now': 'สักครู่นี้',
  '{n}m ago': '{n} นาทีที่แล้ว',
  '{n}h ago': '{n} ชั่วโมงที่แล้ว',

  // ---- Invoice number (renamed from "PO number" 2026-06-24) ----
  'Invoice number': 'เลขที่ใบแจ้งหนี้',
  'Invoice no.': 'เลขที่ใบแจ้งหนี้',
  'e.g. your invoice no.': 'เช่น เลขที่ใบแจ้งหนี้ของคุณ',
  'Project · Invoice no.': 'โปรเจกต์ · เลขที่ใบแจ้งหนี้',
  'Invoice number provided': 'ระบุเลขที่ใบแจ้งหนี้แล้ว',
  'Search invoice, vendor, project…': 'ค้นหาใบแจ้งหนี้ ซัพพลายเออร์ โปรเจกต์…',

  // ---- Delete invoice (admin) ----
  'Delete invoice?': 'ลบใบแจ้งหนี้?',
  'Delete invoice': 'ลบใบแจ้งหนี้',
  'Invoice deleted': 'ลบใบแจ้งหนี้แล้ว',
  'This permanently removes the invoice and trashes its uploaded PDF and proof-of-performance files. It cannot be undone.':
    'การดำเนินการนี้จะลบใบแจ้งหนี้และย้ายไฟล์ PDF กับหลักฐานการส่งมอบงานที่อัปโหลดไปยังถังขยะอย่างถาวร ไม่สามารถย้อนกลับได้',
  'This permanently removes the invoice, trashes its uploaded PDF and proof-of-performance files, AND removes its row from the Account Payable sheet. It cannot be undone. Only do this for a test or mistaken invoice.':
    'การดำเนินการนี้จะลบใบแจ้งหนี้ ย้ายไฟล์ PDF และหลักฐานการส่งมอบงานไปยังถังขยะ และลบแถวออกจากชีตเจ้าหนี้การค้า (Account Payable) อย่างถาวร ไม่สามารถย้อนกลับได้ ใช้เฉพาะกับใบแจ้งหนี้ทดสอบหรือที่สร้างผิดเท่านั้น',
};

const LangContext = createContext({ lang: 'en', setLang: () => {} });

export function LangProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    try { return localStorage.getItem(LANG_KEY) === 'th' ? 'th' : 'en'; } catch (_) { return 'en'; }
  });
  const setLang = useCallback((l) => {
    const v = l === 'th' ? 'th' : 'en';
    try { localStorage.setItem(LANG_KEY, v); } catch (_) {}
    try { document.documentElement.lang = v; } catch (_) {}
    setLangState(v);
  }, []);
  // Keep <html lang> in sync on first mount too.
  React.useEffect(() => { try { document.documentElement.lang = lang; } catch (_) {} }, [lang]);
  return React.createElement(LangContext.Provider, { value: { lang, setLang } }, children);
}

export function useLang() { return useContext(LangContext); }

/** Returns a translate function bound to the current language. */
export function useT() {
  const { lang } = useContext(LangContext);
  return useCallback((s, vars) => {
    let out = (lang === 'th' && TH[s] != null) ? TH[s] : s;
    if (vars) {
      for (const k in vars) out = out.split('{' + k + '}').join(String(vars[k]));
    }
    return out;
  }, [lang]);
}
